import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Pergunta } from 'api/DuvidasFrequentes';

import { StatusMensagens } from 'helpers/enums/AssembleiaStatus';

import { CloseIcon } from 'components/Icons';

import { assembleiaAssociadoActionsLoadDuvidas } from 'store/modules/AssembleiasAssociadoActions';
import {
  IAssembleiaAssociadoEvento,
  IAssembleiasAssociadoReducer,
} from 'store/modules/AssembleiasAssociadoReducer';
import { IAuthUserReducer } from 'store/modules/AuthReducer';
import { IWSAssembleiasReducer } from 'store/modules/WSAssembleiasReducer';
import { IReducer } from 'store/Reducer';

import ErrorHandler from 'helpers/utils/ErrorHandler';

import { Card, Divider, IconButton, Typography } from '@material-ui/core';
import translation from 'locales/translation';
import Configurar from './Configurar';
import Context from './Context';
import Duvidas from './Duvidas';
import Notificacao from './Notificacao';
import Perguntar from './Perguntar';
import { duvidasInitialState, duvidasReducer } from './reducer';
import useStyles from './style';

interface Props {
  onClick?: any;
  isModal?: boolean;
  associado: IAuthUserReducer;
  evento: IAssembleiaAssociadoEvento;
  isSticky?: boolean;
}

export default function DuvidasFrequentes({
  evento,
  associado,
  isSticky,
  onClick,
  isModal,
}: Props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { assembleiasChanges } = useSelector<IReducer, IWSAssembleiasReducer>(
    (state) => state.wsAssembleias
  );
  const { duvidas } = useSelector<IReducer, IAssembleiasAssociadoReducer>(
    (state) => state.assembleias
  );
  const [{ perguntas, message, idEvento, destaque }, action] = useReducer(
    duvidasReducer,
    duvidasInitialState
  );

  const isConvocado = useMemo(() => {
    if (evento.contasDisponiveis && evento.contasDisponiveis.length > 0) {
      return evento.contasDisponiveis.some(
        (conta) => conta.idConvocado !== undefined
      );
    }
    return false;
  }, [evento]);

  const ocultarNomeChat = useMemo(() => {
    return evento.ocultarNomeChat || false;
  }, [evento]);

  const hasPermissionToSend = useMemo((): boolean => {
    if (!evento) return false;
    if (evento.status >= 1) return true;
    return false;
  }, [evento]);

  const atualizarPergunta = useCallback(
    (changes: Pergunta, flgPublica?: boolean) => {
      if (!perguntas) return;
      const _pergunta = Object.assign({}, changes);
      const isPerguntaAssociado =
        _pergunta?.usuarioDuvida.toLowerCase() ===
        associado?.nome.toLowerCase();
      const canBeListed = isPerguntaAssociado || flgPublica;
      const lista = Array.from(perguntas);
      const _index = lista.findIndex(
        (item) => item.idDuvidaFrequente === _pergunta.idDuvidaFrequente
      );
      if (_index >= 0) {
        !canBeListed && lista.splice(_index, 1);
        canBeListed && (lista[_index] = _pergunta);
      } else {
        canBeListed && lista.push(_pergunta);
      }

      action({
        type: 'setPerguntas',
        value: lista,
      });
    },
    [perguntas, associado]
  );

  const isPerguntaAdmin = (pergunta: Pergunta) => {
    return pergunta.flgDestaque;
  };

  const isPerguntaUsuario = useCallback(
    (pergunta: Pergunta) => {
      if (isPerguntaAdmin(pergunta)) return false;
      return (
        pergunta?.usuarioDuvida.toLowerCase() === associado?.nome.toLowerCase()
      );
    },
    [associado]
  );

  const isPerguntaNaoLida = (pergunta: Pergunta) => {
    return pergunta.resposta !== null && !pergunta.lido;
  };

  const setDestaque = (
    value: number | string | null,
    marcarComoLido: boolean
  ) => {
    action({ type: 'setDestaque', value });
    const perguntaElement = document.getElementById(`duvida-${value}`);
    if (!perguntaElement) return;
    perguntaElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    marcarComoLido &&
      window.setTimeout(() => {
        perguntaElement.focus();
      }, 500);
    window.setTimeout(() => {
      action({ type: 'setDestaque', value: null });
    }, 5000);
  };

  const carregarPerguntas = useCallback(async () => {
    dispatch(
      assembleiaAssociadoActionsLoadDuvidas(
        evento.id,
        associado.cpfCnpj,
        undefined,
        ErrorHandler
      )
    );
  }, [evento, associado, dispatch]);

  useEffect(() => {
    if (evento.id !== idEvento) {
      action({ type: 'setEvento', value: evento.id });
      carregarPerguntas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento, idEvento]);

  useEffect(() => {
    action({
      type: 'setPerguntas',
      value: duvidas,
    });
  }, [duvidas]);

  useEffect(() => {
    if (!assembleiasChanges[evento.id]) return;
    const newMessage = assembleiasChanges[evento.id].messenger;
    if (newMessage && newMessage !== null) {
      action({ type: 'setMessage', value: newMessage });
    }
  }, [assembleiasChanges, evento]);

  useEffect(() => {
    if (message) {
      const _message = Object.assign({}, message);
      action({
        type: 'setMessage',
        value: null,
      });
      atualizarPergunta(
        {
          duvida: _message.duvida,
          idDuvidaFrequente: _message.idDuvidaFrequente,
          resposta: _message.resposta || null,
          usuarioDuvida: _message.nomePessoaComentario,
          dataDuvida: _message.dataPergunta,
          dataResposta: '',
          lido: false,
          flgDestaque: _message.flgDestaque,
        },
        _message.flgPublica
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  return (
    <Context.Provider
      value={{
        perguntas,
        atualizarPergunta,
        isPerguntaAdmin,
        isPerguntaUsuario,
        isPerguntaNaoLida,
        ocultarNomeChat,
        cpf: associado.cpfCnpj,
        cooperativa: evento.cooperativa,
        destaque,
        setDestaque,
      }}
    >
      <Card
        component='section'
        className={[
          classes.container,
          isSticky ? classes.sticky : '',
          'scroll-white',
        ]
          .join(' ')
          .trim()}
      >
        <header className={classes.headline}>
          <Typography component='h2' className={classes.title}>
            {translation.DuvidasFrequentes.title}
          </Typography>
          <div className={`${classes.actions} config`}>
            <Configurar />
          </div>
          <div className={classes.actions}>
            <Notificacao />
          </div>
          {isModal && (
            <IconButton
              className={classes.closeIcon}
              color='primary'
              onClick={onClick}
            >
              <CloseIcon size={'10px'} />
            </IconButton>
          )}
        </header>
        <Divider />
        <Duvidas />
        {hasPermissionToSend && isConvocado && (
          <Perguntar
            evento={evento}
            usuario={associado.cpfCnpj}
            idEvento={idEvento}
            isDisabled={evento.statusChat === StatusMensagens.Bloqueado}
          />
        )}
      </Card>
    </Context.Provider>
  );
}
